import DynamicDialog from '@/components/DynamicDialog';
import UserDialog from '@/components/UserDialog';
import { userAtom } from '@/global-store';
import useLogoutFn from '@/hooks/useLogoutFn.jsx';
import ChangePasswordForm from '@/pages/authentication/auth-forms/ChangePasswordForm';
import { Box, Popover, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  IconEdit,
  IconKey,
  IconLogout,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button as UiButton } from '@eye-able/ui';

// ==============================|| PROFILE MENU ||============================== //
const ICON_SIZE = '1.38rem';

function ProfileSection() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [user] = useAtom(userAtom);

  const userInfo = {
    [t('name')]: user?.firstName + ' ' + user?.lastName,
    [t('email')]: user?.email,
    [t('phone')]: user?.phone,
    [t('company')]: user?.company,
    [t('key')]: user?.apiKey,
  };

  const logoutFn = useLogoutFn();

  /**
   * anchorRef is used on different components and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <UiButton
        type='button'
        variant='ghost'
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        ref={anchorRef}
      >
        <IconUser size={ICON_SIZE} />
        <span className='eye:max-xl:sr-only'>{t('profile')}</span>
      </UiButton>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        elevation={4}
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            sx: {
              p: 0,
              borderRadius: theme.borders.borderRadius.xl,
              background: theme.palette.common.white,
              overflow: 'hidden',
            },
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          sx={{
            maxHeight: { xs: 'calc(100vh - 28px)', sm: 'calc(100vh - 65px)' },
            p: 3.5,
            pb: 2.5,
            width: { xs: 'calc(100vw - 28px)', sm: '430px' },
            color: theme.palette.common.black,
            overflow: 'auto',
          }}
        >
          <UiButton
            type='button'
            aria-label={t('closeProfile')}
            onClick={handleClose}
            size='icon'
            variant='ghost'
            style={{
              outlineOffset: '-4px',
              position: 'absolute',
              top: '15px',
              right: '15px',
              zIndex: 5,
            }}
            title={t('closeProfile')}
          >
            <IconX color={theme.palette.common.black} />
          </UiButton>

          <Typography
            component='h3'
            variant='h2'
            sx={{ color: theme.palette.common.black }}
          >
            {t('profileHeader')}
          </Typography>

          <div className='eye:my-2xs eye:flex eye:flex-col eye:gap-3xs eye:mb-s'>
            {Object.entries(userInfo).map(([key, value], index) => (
              <Box key={key} className='eye:my-4'>
                <Box className='eye:flex eye:flex-col' key={index}>
                  <Box className='eye:text-[0.93rem] eye:font-light'>{key}</Box>

                  <Box className='eye:break-words eye:text-[0.96rem] eye:font-medium'>
                    {value || ' - '}
                  </Box>
                </Box>
              </Box>
            ))}
          </div>

          <div className='eye:space-y-4xs eye:mb-2xs'>
            <UserDialog
              hideOrgAdminToggle
              title={t('editProfile')}
              userData={user}
              useUiButton
              activatorProps={{
                className: 'eye:w-full eye:mt-3',
                variant: 'outline',
              }}
              activatorText={
                <>
                  <IconEdit aria-hidden />
                  {t('editProfile')}
                </>
              }
              maxWidth='md'
            />

            <DynamicDialog
              title={t('changePassword')}
              activatorText={
                <>
                  <IconKey aria-hidden />
                  {t('changePassword')}
                </>
              }
              useUiButton
              activatorProps={{
                variant: 'outline',
                className: 'eye:w-full eye:mt-2',
              }}
              hideActions
            >
              {({ handleCancel }) => (
                <Box
                  sx={{
                    my: 2.5,
                    mx: 1,
                  }}
                >
                  <ChangePasswordForm onCancel={handleCancel} />
                </Box>
              )}
            </DynamicDialog>

            <UiButton
              type='button'
              onClick={logoutFn}
              className='eye:w-full eye:my-2'
              variant='outline'
            >
              <IconLogout aria-hidden />
              {t('logOutSB')}
            </UiButton>
          </div>
        </Box>
      </Popover>
    </>
  );
}

export default ProfileSection;
